<template>
  <div>
    <b-modal id="approve-modal" title="Approve Workload" @ok="submitWorkloadApproval">
      <p>Press 'OK' to mark this workload as approved.</p>
      <p>
        You may also leave a note with this status change, which will be visible
        to the faculty member who made this workload request.
      </p>
      <b-form-textarea v-model="approveNotes" placeholder="Notes (optional)" rows="3"></b-form-textarea>
    </b-modal>

    <b-modal id="deny-modal" title="Resubmit Workload" @ok="submitWorkloadDenial">
      <p>Press 'OK' to mark this workload as denied.</p>
      <p v-if="isSubmitted">
        The workload will be marked as 'Resubmit' and the faculty member will
        have the chance to edit and resubmit it.
      </p>
      <p>
        You may also leave a note with this status change, which will be visible
        to the faculty member who made this workload request.
      </p>
      <b-form-textarea v-model="denyNotes" placeholder="Notes (optional)" rows="3"></b-form-textarea>
    </b-modal>

    <div v-if="workload">
      <h3>
        Workload - {{ workload.asurite.firstName }}
        {{ workload.asurite.lastName }}
        <span v-if="workload.timePeriod">({{ workload.timePeriod.description }})</span>
      </h3>

      <b-alert show v-if="!isNewestWorkload" variant="danger" class="my-2 mx-2">
        <div>
          <font-awesome-icon icon="exclamation-triangle"></font-awesome-icon>
          There are newer versions of this workload.
        </div>
        <hr />
        <div>
          The latest status is
          {{ newestWorkload.currentStatus.workloadStatusDefinition.name }} on
          {{ newestWorkload.currentStatus.createdDate | formatDateTime }} by {{ newestWorkload.currentStatus.createdBy }}.
        </div>

        <div>
          <router-link :to="{
            name: 'workload',
            params: { workloadId: newestWorkload.workloadId },
          }">Click here to view the latest version.</router-link>
        </div>

        <div v-if="newestApprovedWorkload && newestApprovedWorkload.workloadId !== workload.workloadId">
          <hr />
          The latest approved workload is
          {{ newestApprovedWorkload.currentStatus.workloadStatusDefinition.name }} on
          {{ newestApprovedWorkload.currentStatus.createdDate | formatDateTime }} by {{
            newestApprovedWorkload.currentStatus.createdBy }}
          <div>
            <router-link :to="{
              name: 'workload',
              params: { workloadId: newestApprovedWorkload.workloadId },
            }">Click here to view the latest approved version.</router-link>
          </div>
        </div>
      </b-alert>

      <b-alert show :variant="statusAlertVariant" class="my-2 mx-2" v-if="currentWorkloadStatus">
        <span>Current Status:
          <span class="font-weight-bold" v-if="currentWorkloadStatus">{{
            currentWorkloadStatus.workloadStatusDefinition.name
          }}</span></span>
        <div v-if="currentWorkloadStatus.notes" class="mx-4">
          <hr />
          Comments:
          <span class="font-italic">{{ currentWorkloadStatus.notes }}</span>
        </div>
      </b-alert>

      <b-alert :show="workload.buyoutRequests.length > 0 && unapprovedBuyouts == 0" class="my-2 mx-2" variant="success">
        Buyout Requests: <b>All Approved</b>
      </b-alert>

      <b-alert :show="workload.buyoutRequests.length > 0 && unapprovedBuyouts > 0" class="my-2 mx-2" variant="danger">
        Buyout Requests: There are {{ unapprovedBuyouts }} pending buyout
        requests
      </b-alert>

      <b-alert variant="success" :show="workload.buyoutRequests.length == 0" class="my-2 mx-2">
        Buyout Requests: There are no buyout requests for this workload.
      </b-alert>

      <b-alert :show="workload.administrativeRequests.length > 0 &&
        unapprovedAdminRequests == 0
        " class="my-2 mx-2" variant="success">
        Administrative Requests: <b>All Approved</b>
      </b-alert>

      <b-alert :show="workload.administrativeRequests.length > 0 &&
        unapprovedAdminRequests > 0
        " class="my-2 mx-2" variant="danger">
        Administrative Requests: There are {{ unapprovedAdminRequests }} pending
        administrative requests
      </b-alert>

      <b-alert variant="success" :show="workload.administrativeRequests.length == 0" class="my-2 mx-2">
        Administrative Requests: There are no administrative requests for this
        workload.
      </b-alert>

      <b-card class="mb-4">
        <b-row>
          <b-col sm="4">
            <div>
              <label class="font-weight-bold">Asurite Id:</label>
              {{ workload.asuriteId }}
            </div>
            <div v-if="primaryJob && primaryJob.job && primaryJob.job.jobAcademicRank
              ">
              <label class="font-weight-bold">Job Academic Rank:</label>
              {{ primaryJob.job.jobAcademicRank.description }}
            </div>
            <div v-if="primaryJob && primaryJob.tenureStatus">
              <label class="font-weight-bold">Tenure Status:</label>
              {{ primaryJob.tenureStatus.description }}
            </div>
          </b-col>
          <b-col sm="4">
            <div v-if="primaryJob && primaryJob.job">
              <label class="font-weight-bold">Title:</label>
              {{ primaryJob.job.description }}
            </div>
            <div>
              <label class="font-weight-bold">Paygroup:</label>
              {{ employeeType }}
            </div>
            <div v-if="primaryJob && primaryJob.fte">
              <label class="font-weight-bold">FTE:</label> {{ primaryJob.fte }}
            </div>
          </b-col>
          <b-col sm="4">
            <div v-if="primaryJob && primaryJob.department">
              <label class="font-weight-bold">Department:</label>
              {{ primaryJob.department.description }}
            </div>
            <div v-if="primaryJob && primaryJob.department">
              <label class="font-weight-bold">Department Id:</label>
              {{ primaryJob.department.deptId }}
            </div>
          </b-col>
        </b-row>

        <!-- <b-row>
          <b-col sm="4">

          </b-col>
          <b-col sm="8">
            <label class="font-weight-bold">Other Job Assignments</label>
          </b-col>
        </b-row>
                  
          <div v-for="(job, index) in workload.employeeJobs" :key="index">
            <b-row v-if="job.jobIndicator != 'P'">
              <b-col sm="4"></b-col>
              <b-col sm="4">
                <div><label class="font-weight-bold">Title:</label> {{ job.job.description }}</div>
                <div><label class="font-weight-bold">FTE:</label> {{job.fte}}</div>
              </b-col>
              <b-col sm="4">
                <div><label class="font-weight-bold">Department:</label> {{job.department.description}}</div>
                <div><label class="font-weight-bold">Department Id:</label> {{job.department.deptId}}</div>
              </b-col>
            </b-row>
          </div> -->
      </b-card>

      <b-container>
        <workload-type-summary :workload-types="workload.workloadTypes"></workload-type-summary>
        <b-row>
          <b-col>
            <b-alert :show="workload.differsFromPrevious" variant="warning">This distribution differs from the last
              approved
              workload.</b-alert>
          </b-col>
        </b-row>
      </b-container>

      <div>
        <b-card class="mt-3">
          <h4>Planned Courses</h4>
          <p>
            These are courses that were entered when the workload was submitted.
          </p>
          <b-alert variant="warning" :show="contributingCourseCount !== coursesNeeded">
            This workload needs
            {{ coursesNeeded - contributingCourseCount }} more course{{
              coursesNeeded - contributingCourseCount > 1 ? "s" : ""
            }}
            to match its teaching percentage.
          </b-alert>

          <b-row>
            <b-col sm="4" v-for="timePeriodTerm in workload.timePeriod.timePeriodTerms" :key="timePeriodTerm.termId">
              <b-card class="my-1 workload-term-card">
                <h5 v-if="timePeriodTerm.term">
                  {{ timePeriodTerm.term.description }}
                </h5>

                <div class="no-courses-text" v-if="!termCourses[timePeriodTerm.term.termCode].length">
                  No classes have been added for this semester.
                </div>

                <b-table striped hover small :fields="courseFields" :items="termCourses[timePeriodTerm.term.termCode]"
                  v-if="termCourses[timePeriodTerm.term.termCode].length">
                  <template v-slot:cell(contributesToTeaching)="data">
                    <font-awesome-icon icon="minus-circle" v-b-tooltip.hover
                      title="This course does not contribute to Teaching %."
                      v-if="!data.item.contributesToTeaching"></font-awesome-icon>
                  </template>
                  <template v-slot:cell(new)="data">
                    <font-awesome-icon icon="exclamation-triangle" v-b-tooltip.hover
                      title="This course does not match any courses currently in the database."
                      v-if="data.item.new"></font-awesome-icon>
                  </template>
                  <template v-slot:cell(course)="data">
                    {{ data.item.subjectCode }} {{ data.item.catalogNumber }} -
                    {{ data.item.courseTitle }}
                  </template>
                  <template v-slot:cell(notes)="data">
                    <div class="text-right" v-if="data.item.notes">
                      <font-awesome-icon icon="sticky-note" v-b-tooltip.hover
                        :title="data.item.notes"></font-awesome-icon>
                    </div>
                  </template>
                </b-table>
              </b-card>
            </b-col>
          </b-row>
        </b-card>

        <b-card class="mt-3" v-if="workload.asurite &&
          workload.asurite.classRecords &&
          workload.asurite.classRecords.length
          ">
          <h4>Actual Courses</h4>

          <b-alert variant="info" show>These are the courses from PeopleSoft that were actually taught.
            Courses grouped with the <font-awesome-icon icon="link" /> icon are
            combined courses.
          </b-alert>
          <b-alert variant="warning" :show="workload.asurite.classRecords.length < coursesNeeded">
            This workload needs
            {{ coursesNeeded - workload.asurite.classRecords.length }} more
            course{{
              coursesNeeded - workload.asurite.classRecords.length > 1
              ? "s"
              : ""
            }}
            to match its teaching percentage.
          </b-alert>

          <b-row>
            <b-col sm="4" v-for="timePeriodTerm in workload.timePeriod.timePeriodTerms"
              :key="timePeriodTerm.term.termCode">
              <b-card class="my-1 workload-term-card">
                <!-- term name -->
                <h5 v-if="timePeriodTerm.term">
                  {{ timePeriodTerm.term.description }}
                </h5>
                <div v-if="timePeriodTerm.term.termCode.endsWith('1') &&
                  !showSpringClasses
                  ">
                  <b-alert show>Classes will be visible around September 1.</b-alert>
                </div>
                <div v-else-if="timePeriodTerm.term.termCode.endsWith('4') &&
                  !showSummerClasses
                  ">
                  <b-alert show>Classes will be visible around January 1.</b-alert>
                </div>
                <div v-else>
                  <div class="no-courses-text" v-if="!termClassRecords[timePeriodTerm.term.termCode].length
                    ">
                    No courses were taught this semester.
                  </div>

                  <combined-courses :courses="termClassRecords[timePeriodTerm.term.termCode]" />
                </div>
              </b-card>
            </b-col>
          </b-row>
        </b-card>
      </div>

      <div class="mt-3">
        <b-card>
          <h4>Buyout Requests</h4>
          <div>
            <b-table striped hover :items="workload.buyoutRequests" :fields="buyoutFields" sort-by="name" responsive
              show-empty>
              <template v-slot:cell(status)="data">
                {{ data.item.currentStatus.statusName }} (
                {{ data.item.currentStatus.createdDate | formatDateTime }} )
              </template>
              <template v-slot:cell(grants)="data">
                <div v-for="(grant, index) in data.item.grants" :key="index">
                  {{ grant.description }}
                </div>
              </template>
              <template v-slot:cell(details)="data">
                <router-link v-if="isBuyoutViewer || isBuyoutManager" :to="{
                  name: 'buyoutRequestDetails',
                  params: { id: data.item.id },
                }">Details</router-link>
              </template>
            </b-table>
          </div>
        </b-card>
      </div>

      <div class="mt-3">
        <b-card>
          <h4>Administrative Requests</h4>
          <div>
            <b-table striped :items="workload.administrativeRequests" :fields="adminFields" :filter="filter"
              @filtered="onFiltered" small show-empty :tbody-transition-props="transProps"
              empty-filtered-text="There are no requests that match your search criteria.">
              <template v-slot:cell(createdDate)="data">
                <template v-if="data.item.createdDate">
                  {{ data.item.createdDate | formatDateTime }}
                </template>
                <template v-else> Not Submitted </template>
              </template>
              <template v-slot:cell(percent)="data">
                {{ data.item.administrativePercent || 0 }}
              </template>
              <template v-slot:cell(status)="data">
                {{ data.item.currentStatus.statusName }}
              </template>
              <template v-slot:cell(supervisor)="data">
                {{ data.item.supervisor.fullName }}
              </template>
              <template v-slot:cell(courseReleases)="data">
                <span v-if="data.item.numberOfCourses">{{ data.item.numberOfCourses }} courses</span>
                <span v-else>None</span>
              </template>

              <template v-slot:cell(stipend)="data">
                {{ data.item.stipend | trueOrFalse }}
              </template>
              <template v-slot:cell(controls)="data">
                <router-link v-if="isBuyoutViewer || isBuyoutManager" :to="{
                  name: 'adminRequestDetails',
                  params: { id: data.item.id },
                }">Details</router-link>
              </template>
            </b-table>
          </div>
        </b-card>
      </div>

      <div class="mt-3" v-if="workload.timePeriod">
        <b-card>
          <h4>Course Equivalents</h4>

          <workload-course-equivalents :courseEquivalents="courseEquivalents"></workload-course-equivalents>
        </b-card>
      </div>

      <div v-for="workloadType in workload.workloadTypes" :key="workloadType.workloadTypeDefinitionId" class="mt-3">
        <b-card v-if="workloadType.workloadTypeDefinition.name != 'Teaching'">
          <h4>{{ workloadType.workloadTypeDefinition.name }}</h4>
          <hr />
          <div class="mx-3">{{ workloadType.notes }}</div>
          <div class="mx-3 font-italic" v-if="!workloadType.notes">
            No text was entered for this section.
          </div>
        </b-card>
      </div>

      <div class="mt-3">
        <b-card>
          <h4>Supporting Documents</h4>
          <workload-files :files="mappedFiles" @fileAdded="addFile" @fileRemoved="removeFile"></workload-files>
        </b-card>
      </div>

      <div class="mt-3" v-if="isWorkloadsManager">
        <b-card>
          <h4>Employee Notes</h4>
          <employee-notes :notes="employeeNotes" @noteAdded="addNote"></employee-notes>
        </b-card>
      </div>

      <b-alert show :variant="statusAlertVariant" class="my-2 mx-2" v-if="currentWorkloadStatus">
        <span>Current Status:
          <span class="font-weight-bold" v-if="currentWorkloadStatus">{{
            currentWorkloadStatus.workloadStatusDefinition.name
          }}</span></span>
        <div v-if="currentWorkloadStatus.notes" class="mx-4">
          <hr />
          Comments:
          <span class="font-italic">{{ currentWorkloadStatus.notes }}</span>
        </div>
      </b-alert>

      <b-alert :show="workload.buyoutRequests.length > 0 && unapprovedBuyouts == 0" class="my-2 mx-2" variant="success">
        Buyout Requests: <b>All Approved</b>
      </b-alert>

      <b-alert :show="workload.buyoutRequests.length >= 0 && unapprovedBuyouts > 0" class="my-2 mx-2" variant="danger">
        Buyout Requests: There are {{ unapprovedBuyouts }} pending buyout
        requests
      </b-alert>

      <b-alert variant="success" :show="workload.buyoutRequests.length == 0" class="my-2 mx-2">
        Buyout Requests: There are no buyout requests for this workload.
      </b-alert>

      <b-alert :show="workload.administrativeRequests.length > 0 &&
        unapprovedAdminRequests == 0
        " class="my-2 mx-2" variant="success">
        Administrative Requests: <b>All Approved</b>
      </b-alert>

      <b-alert :show="workload.administrativeRequests.length > 0 &&
        unapprovedAdminRequests > 0
        " class="my-2 mx-2" variant="danger">
        Administrative Requests: There are {{ unapprovedAdminRequests }} pending
        administrative requests
      </b-alert>

      <b-alert variant="success" :show="workload.administrativeRequests.length == 0" class="my-2 mx-2">
        Administrative Requests: There are no administrative requests for this
        workload.
      </b-alert>

      <b-alert show v-if="!isNewestWorkload" variant="danger" class="my-2 mx-2">
        <div>
          <font-awesome-icon icon="exclamation-triangle"></font-awesome-icon>
          There are newer versions of this workload.
        </div>
        <hr />
        <div>
          The latest status is
          {{ newestWorkload.currentStatus.workloadStatusDefinition.name }} on
          {{ newestWorkload.currentStatus.createdDate | formatDateTime }} by {{ newestWorkload.currentStatus.createdBy }}.
        </div>

        <div>
          <router-link :to="{
            name: 'workload',
            params: { workloadId: newestWorkload.workloadId },
          }">Click here to view the latest version.</router-link>
        </div>

        <div v-if="newestApprovedWorkload && newestApprovedWorkload.workloadId !== workload.workloadId">
          <hr />
          The latest approved workload is
          {{ newestApprovedWorkload.currentStatus.workloadStatusDefinition.name }} on
          {{ newestApprovedWorkload.currentStatus.createdDate | formatDateTime }} by {{
            newestApprovedWorkload.currentStatus.createdBy }}
          <div>
            <router-link :to="{
              name: 'workload',
              params: { workloadId: newestApprovedWorkload.workloadId },
            }">Click here to view the latest approved version.</router-link>
          </div>
        </div>
      </b-alert>

      <div class="text-right">
        <b-button v-if="isWorkloadsManager &&
          currentWorkloadStatus &&
          currentWorkloadStatus.workloadStatusDefinition.name != 'Approved'
          " v-b-modal.approve-modal variant="primary" class="mx-1" :disabled="!isNewestWorkload">Approve</b-button>
        <b-button v-if="isWorkloadsManager &&
          currentWorkloadStatus &&
          currentWorkloadStatus.workloadStatusDefinition.name != 'Resubmit'
          " v-b-modal.deny-modal variant="primary" class="mx-1" :disabled="!isNewestWorkload">Resubmit</b-button>
        <b-button :to="{ name: 'workloadEdit', params: { workloadId: workloadId } }" variant="primary" class="mx-1"
          v-if="workload.asuriteId == user.userId && isWorkloadsSubmitter" :disabled="!isNewestWorkload">
          {{ editText }}
        </b-button>
      </div>

      <div class="mt-5" v-if="isWorkloadsManager">
        <b-card>
          <b-btn @click="viewPastWorkloads = !viewPastWorkloads" class="previous-workload-button">
            {{ viewPastWorkloads ? "Hide History" : "Show History" }}
          </b-btn>
          <b-collapse v-model="viewPastWorkloads" class="mt-2" id="workload-collapse">
            <workload-list :workloads="workloadHistory" :workloadTypeDefinitions="workloadTypeDefinitions"
              :showStatusField="true" :showDateField="true" :meta="historyMeta"></workload-list>
          </b-collapse>
        </b-card>

        <b-card>
          <b-btn @click="viewClassHistory = !viewClassHistory" class="previous-workload-button">
            {{ viewClassHistory ? "Hide Class History" : "Show Class History" }}
          </b-btn>
          <b-collapse v-model="viewClassHistory" class="mt-2" id="workload-collapse">
            <b-row>
              <b-col sm="12" v-for="timePeriodTerm in classHistoryTerms" :key="timePeriodTerm.termCode">
                <b-card class="my-1 workload-term-card">
                  <h5 v-if="timePeriodTerm">
                    {{ timePeriodTerm.description }}
                  </h5>

                  <div class="no-courses-text" v-if="!termClassHistory[timePeriodTerm.termCode].length">
                    No courses were taught this semester.
                  </div>

                  <combined-courses :courses="termClassHistory[timePeriodTerm.termCode]" />
                </b-card>
              </b-col>
            </b-row>
          </b-collapse>
        </b-card>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import CombinedCourses from "../../components/workloads/combined-courses";
import WorkloadTypeSummary from "../../components/workloads/workload-type-summary.vue";
import WorkloadFiles from "@/components/workloads/workload-files";
import EmployeeNotes from "@/components/workloads/employee-notes";
import workloadsApi from "../../api/workloads";
import employeeApi from "../../api/employees";
import WorkloadList from "@/components/workloads/workload-list";
import * as _ from "lodash"; // eslint-disable-line no-unused-vars
import roleChecks from "../../misc/role-checks";
import ToastrSettings from "../../settings/toastr-settings";
import toastr from "toastr";
// import adminRequestsVue from "../admin-requests/admin-requests.vue";
import WorkloadCourseEquivalents from "@/components/workloads/workload-course-equivalents";
import courseEquivalentsApi from "../../api/courseEquivalents.js";

export default {
  mixins: [roleChecks],

  data() {
    return {
      transProps: {
        // Transition name
        name: "fade"
      },
      contributingCourses: [],
      nonContributingCourses: [],
      workloadTypeDefinitions: [],
      viewPastWorkloads: false,
      viewClassHistory: false,
      employeeNotes: [],
      courseEquivalents: [],
      courseFields: [
        { key: "new", label: "" },
        { key: "contributesToTeaching", label: "" },
        { key: "course" },
        { key: "units" },
        { key: "notes", label: "" }
      ],
      classRecordFields: [
        { key: "course" },
        { key: "scheduleLineNumber", label: "SLN" },
        { key: "classSectionCode", label: "Section" },
        { key: "combinedSectionId", label: "Combined" },
        { key: "enrollmentTotal", label: "Enrollment" }
      ],
      buyoutFields: [
        { key: "term.description", sortable: true, label: "Semester" },
        { key: "numberOfClasses", sortable: true },
        { key: "grants", sortable: true },
        { key: "status", sortable: true },
        { key: "details", label: "" }
      ],
      classHistory: [],
      approveNotes: null,
      denyNotes: null,
      workloadHistory: [],

      currentPage: 1,
      perPage: 20,
      filter: null,
      totalRows: 0,

      // selectedRequests: [],
      items: [],
      adminFields: [
        { key: "status", label: "Status" },
        { key: "percent", label: "Percent" },
        { key: "supervisor", label: "Supervisor" },
        { key: "courseReleases", sortable: true, label: "Course Releases" },
        { key: "stipend", sortable: true, label: "Stipend" },
        { key: "notes", label: "Explanation of Duties" },
        { key: "createdDate", label: "Date" },
        { key: "controls", label: "" }
      ],

      // Sept. 20, 2022, Marina
      schedulePrint: false,
      historyMeta: {
        currentPage: 1,
        perPage: 20,
        totalRows: 0,
        sortBy: "asuriteId",
        sortDesc: false
      }
    };
  },

  components: {
    WorkloadFiles,
    WorkloadList,
    CombinedCourses,
    EmployeeNotes,
    WorkloadTypeSummary,
    WorkloadCourseEquivalents
  },

  props: {
    workloadId: {
      required: true
    }
  },

  computed: {
    ...mapGetters("workloads", ["getWorkloadById"]),

    ...mapState({
      user: state => state.auth.user,
      employeeJobs: state => state.workloadForm.employeeJobs,
      files: state => state.workloads.files
    }),

    showSpringClasses() {
      //springClassDate = new DateTime(x.TermStart.Value.Year - 1, 9, 1);
      var semester = _.find(
        this.workload.timePeriod.timePeriodTerms,
        timePeriodTerm => {
          return timePeriodTerm.term.termCode.endsWith("1");
        }
      );

      var semesterYear = parseInt(semester.term.termEnd.slice(0, 4)) - 1;
      var openDate = new Date(semesterYear + "-9-1");
      return new Date() >= openDate;
    },

    showSummerClasses() {
      //springClassDate = new DateTime(x.TermStart.Value.Year - 1, 9, 1);
      var semester = _.find(
        this.workload.timePeriod.timePeriodTerms,
        timePeriodTerm => {
          return timePeriodTerm.term.termCode.endsWith("4");
        }
      );

      var semesterYear = parseInt(semester.term.termEnd.slice(0, 4));
      var openDate = new Date(semesterYear + "-1-1");
      return new Date() >= openDate;
    },

    mappedFiles() {
      return _.map(this.workload.workloadFiles, function (workloadFile) {
        return {
          fileId: workloadFile.file.fileId,
          name: workloadFile.file.fileName,
          createdBy: workloadFile.file.createdBy,
          createdDate: workloadFile.file.createdDate,
          _rowVariant: workloadFile.file._rowVariant
            ? workloadFile.file._rowVariant
            : null
        };
      });
    },

    workload: function () {
      return this.getWorkloadById(this.workloadId);
    },

    getContributingCourses: function () {
      var contributingCourses = [];
      var nonContributingCourses = [];
      var that = this;

      if (this.workload) {
        _.forEach(this.workload.timePeriod.timePeriodTerms, function (
          timePeriodTerm
        ) {
          if (timePeriodTerm.term) {
            var filteredItems = _.filter(that.workload.courses, function (
              course
            ) {
              return course.term.termCode === timePeriodTerm.term.termCode;
            });
            _.forEach(filteredItems, item => {
              if (!item.contributesToTeaching) {
                item._rowVariant = "danger";
                nonContributingCourses.push(item);
              } else {
                contributingCourses.push(item);
              }
            });
          }
        });
      }
      return contributingCourses;
    },

    unapprovedBuyouts: function () {
      var unapprovedTotal = 0;

      if (this.workload) {
        var i;
        var tmpBuyouts = this.workload.buyoutRequests;
        var bLen = tmpBuyouts.length;
        var aBuyout;

        for (i = 0; i < bLen; i++) {
          aBuyout = tmpBuyouts[i];

          if (aBuyout.currentStatus.statusName != "Approved") unapprovedTotal++;
        }
      }

      return unapprovedTotal;
    },

    unapprovedAdminRequests: function () {
      var unapprovedTotal = 0;

      if (this.workload) {
        var i;
        var tmpAdminRequests = this.workload.administrativeRequests;
        var aLen = tmpAdminRequests.length;
        var aRequest;

        for (i = 0; i < aLen; i++) {
          aRequest = tmpAdminRequests[i];

          if (aRequest.currentStatus.statusName != "Approved")
            unapprovedTotal++;
        }
      }
      return unapprovedTotal;
    },

    newestWorkload: function () {
      if (this.workloadHistory.length) {
        var newestWorkload = _.maxBy(this.workloadHistory, function (workload) {
          return new Date(workload.createdDate);
        });

        return {
          workloadId: newestWorkload.workloadId,
          currentStatus: newestWorkload.currentStatus
        };
      }
      return null;
    },

    newestApprovedWorkload: function () {
      // filter workload history to get approved workloads
      var approvedWorkloads = _.filter(this.workloadHistory, function (
        workload
      ) {
        return (
          workload.currentStatus.workloadStatusDefinition.name === "Approved"
        );
      });

      return _.maxBy(approvedWorkloads, function (workload) {
        return new Date(workload.createdDate);
      });
    },

    isNewestWorkload: function () {
      if (this.workload && this.newestWorkload) {
        return this.workload.workloadId === this.newestWorkload.workloadId;
      }
      return true;
    },

    isNewerResubmittedWorkload: function () {
      if (this.workload && !this.isNewestWorkload) {
        return (
          this.newestWorkload.currentStatus.workloadStatusDefinition.name ===
          "Resubmitted" ||
          this.newestWorkload.currentStatus.workloadStatusDefinition.name ===
          "Revision"
        );
      }
      return false;
    },

    isNewerApprovedWorkload: function () {
      if (this.workload && !this.isNewestWorkload) {
        return (
          this.newestWorkload.currentStatus.workloadStatusDefinition.name ===
          "Approved"
        );
      }
      return false;
    },

    currentWorkloadStatus: function () {
      if (this.workload) {
        var currentStatus = _.maxBy(this.workload.workloadStatuses, function (
          workloadStatus
        ) {
          return new Date(workloadStatus.createdDate);
        });
        return currentStatus;
      }
      return null;
    },

    // dictionary with termId as key and array of courses as value
    termCourses: function () {
      var that = this;
      var returnDict = {};
      var schedulePrint = [];

      _.forEach(this.workload.timePeriod.timePeriodTerms, function (
        timePeriodTerm
      ) {
        if (timePeriodTerm.term) {
          var filteredItems = _.filter(that.workload.courses, function (course) {
            return course.term.termCode === timePeriodTerm.term.termCode;
          });
          _.forEach(filteredItems, item => {
            if (!item.contributesToTeaching) {
              item._rowVariant = "danger";
              that.nonContributingCourses.push(item);
            } else {
              that.contributingCourses.push(item);
            }
          });

          returnDict[timePeriodTerm.term.termCode] = filteredItems;
        }
      });
      return returnDict;
    },

    termClassRecords: function () {
      var that = this;
      var returnDict = {};
      _.forEach(this.workload.timePeriod.timePeriodTerms, function (
        timePeriodTerm
      ) {
        var filteredItems = _.filter(
          that.workload.asurite.classRecords,
          function (course) {
            return course.termCode === timePeriodTerm.term.termCode;
          }
        );
        _.forEach(filteredItems, item => {
          if (!item.contributesToTeaching || !item.schedulePrint)
            // {
            item._rowVariant = "danger";
          //   that.nonContributingCourses.push(item);
          // }
          // else
          // {
          //   that.contributingCourses.push(item);
          // }
        });

        returnDict[timePeriodTerm.term.termCode] = filteredItems;
      });

      return returnDict;
    },

    classHistoryTerms: function () {
      return _.orderBy(
        _.uniqWith(
          _.map(this.classHistory, item => {
            return {
              termCode: item.termCode,
              description: item.termDescription
            };
          }),
          _.isEqual
        ),
        ["termCode"],
        ["desc"]
      );
    },

    termClassHistory: function () {
      var returnDict = {};

      _.forEach(this.classHistoryTerms, timePeriodTerm => {
        returnDict[timePeriodTerm.termCode] = _.filter(
          this.classHistory,
          function (classRecord) {
            return classRecord.termCode === timePeriodTerm.termCode;
          }
        );
      });
      return returnDict;
    },

    isSubmitted: function () {
      if (this.currentWorkloadStatus) {
        return (
          this.currentWorkloadStatus.workloadStatusDefinition.name ===
          "Submitted"
        );
      }
      return false;
    },

    statusAlertVariant: function () {
      if (this.currentWorkloadStatus) {
        if (
          this.currentWorkloadStatus.workloadStatusDefinition.name ===
          "Approved"
        ) {
          return "success";
        }
        if (
          this.currentWorkloadStatus.workloadStatusDefinition.name === "Denied"
        ) {
          return "danger";
        }
        if (
          this.currentWorkloadStatus.workloadStatusDefinition.name ===
          "Resubmit"
        ) {
          return "warning";
        }
      }
      return "info";
    },

    editText: function () {
      if (this.currentWorkloadStatus) {
        if (
          this.currentWorkloadStatus.workloadStatusDefinition.name ===
          "Resubmit"
        ) {
          return "Resubmit";
        }
        if (
          this.currentWorkloadStatus.workloadStatusDefinition.name ===
          "Approved" ||
          this.currentWorkloadStatus.workloadStatusDefinition.name === "Denied"
        ) {
          return "Revise";
        }
      }
      return "Edit";
    },

    primaryEmployeeJob: function () {
      if (!this.workload.employeeJobs.length) {
        return null;
      }
      var primary = _.find(this.workload.employeeJobs, function (employeeJob) {
        return employeeJob.jobIndicator === "P";
      });
      if (primary) {
        return primary;
      }
      return this.workload.employeeJobs[0];
    },

    approvedCourseEquivalents: function () {
      const approved = _.filter(this.courseEquivalents, function (
        courseEquivalent
      ) {
        return courseEquivalent.status === "Approved";
      });

      const numberOfCourses = _.sumBy(approved, function (courseEquivalent) {
        return courseEquivalent.numberOfCourses;
      });

      return numberOfCourses || 0;
    },

    coursesNeeded: function () {
      var offset = 0 - this.approvedCourseEquivalents;
      var courses = 0;
      var fte = this.primaryEmployeeJob ? this.primaryEmployeeJob.fte : 1;
      var teachingWorkloadType = _.find(this.workload.workloadTypes, function (
        workloadType
      ) {
        return workloadType.workloadTypeDefinition.name === "Teaching";
      });
      if (teachingWorkloadType && this.primaryEmployeeJob) {
        if (
          this.primaryEmployeeJob.paygroup === "FSW" ||
          this.primaryEmployeeJob.paygroup === "SAL"
        ) {
          var roundedToFive =
            Math.round((teachingWorkloadType.value * 100) / 5) * 5;

          if (roundedToFive <= 0) {
            courses = 0;
          } else if (roundedToFive <= 10) {
            courses = 1;
          } else if (roundedToFive <= 20) {
            courses = 2;
          } else if (roundedToFive <= 25) {
            courses = 3;
          } else if (roundedToFive <= 30) {
            courses = 4;
          } else if (roundedToFive <= 40) {
            courses = 5;
          } else if (roundedToFive <= 50) {
            courses = 6;
          } else if (roundedToFive <= 55) {
            courses = 7;
          } else if (roundedToFive <= 65) {
            courses = 8;
          } else if (roundedToFive <= 70) {
            courses = 9;
          } else if (roundedToFive <= 80) {
            courses = 10;
          } else if (roundedToFive <= 85) {
            courses = 11;
          } else if (roundedToFive > 85) {
            courses = 12;
          }

          return Math.round(courses * fte) + offset;
        } else {
          courses = Math.ceil(teachingWorkloadType.value * 10);
          return Math.round(courses * fte) + offset;
        }
      }
      return 0;
    },

    // number of courses that contribute to Teaching %
    contributingCourseCount: function () {
      var count = 0;
      _.forEach(this.workload.courses, function (course) {
        if (course.contributesToTeaching) {
          count += 1;
        }
      });
      return count;
    },

    primaryJob: function () {
      // return _.find(this.workload.asurite.employeeJobs, function(employeeJob) {
      //   return employeeJob.jobIndicator === "P";
      // });
      return this.primaryEmployeeJob;
    },

    employeeType: function () {
      if (this.primaryJob) {
        if (this.primaryJob.paygroup === "SAL") {
          return "Fiscal Year";
        }
        if (
          this.primaryJob.paygroup === "ACD" ||
          this.primaryJob.paygroup === "A12"
        ) {
          return "Academic Year";
        }
      }
      return "";
    }
  },

  methods: {
    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },

    ...mapActions("workloads", [
      "populateWorkloadById",
      "approveWorkload",
      "sendApprovalEmail",
      "denyWorkload",
      "sendDenyEmail",
      "addWorkloadFiles"
    ]),

    ...mapActions("workloadForm", ["getEmployeeJobs"]),

    submitWorkloadApproval: function () {
      var that = this;
      toastr.options = ToastrSettings;
      this.approveWorkload([this.workloadId, this.approveNotes])
        .then(function () {
          // Toastr Message
          toastr.success("Workload approved.", "Success");
          that.sendApprovalEmail(that.workloadId);
        })
        .catch(function (error) {
          console.log(error);
          toastr.error("Error approving workload.", "Error");
        });
    },

    submitWorkloadDenial: function () {
      var that = this;
      toastr.options = ToastrSettings;
      this.denyWorkload([this.workloadId, this.denyNotes])
        .then(function () {
          // Toastr Message
          toastr.success("Workload denied.", "Success");
          that.sendDenyEmail(that.workloadId);
        })
        .catch(function (error) {
          console.log(error);
          toastr.error("Error denying workload.", "Error");
        });
    },

    addFile(files) {
      var that = this;
      this.$emit("fileAdded", files);
      _.forEach(files, function (file) {
        that.files.push(file);
        that.workload.workloadFiles.unshift({
          workloadId: that.workload.workloadId,
          file: {
            fileName: file.name,
            createdBy: that.user.userId,
            createdDate: new Date(),
            _rowVariant: "success"
          }
        });
      });

      this.addWorkloadFiles(this.workload.workloadId);
    },

    removeFile(file) {
      this.$emit("fileRemoved", file);
    },

    addNote(note) {
      note.asuriteId = this.workload.asuriteId;
      employeeApi.postEmployeeNote(note).then(() => {
        this.loadNotes(this.workload.asuriteId);
      });
    },

    loadNotes(asuriteId) {
      employeeApi.getEmployeeNotes(asuriteId).then(response => {
        this.employeeNotes = response.data;
      });
    },

    loadClassHistory(asuriteId) {
      employeeApi.getClasses(asuriteId).then(response => {
        this.classHistory = response.data;
      });
    },

    loadCourseEquivalents(timePeriod, asuriteId) {
      // get a list of termIds from timeperiod
      var termIds = _.map(timePeriod.timePeriodTerms, function (timePeriodTerm) {
        return timePeriodTerm.termId;
      });

      // for each termId in termIds, call the api to get course equivalents
      _.forEach(termIds, termId => {
        courseEquivalentsApi
          .getByTermAndAsurite(termId, asuriteId)
          .then(response => {
            //this.courseEquivalents.push(response.data);
            this.courseEquivalents.push(...response.data);
          });
      });
    }
  },

  watch: {
    // requests: function() {
    //   if (this.requests.length) {
    //     this.selectedRequests = this.requests;
    //     this.totalRows = this.requests.length;
    //   }
    // },

    workload: function () {
      var that = this;
      if (!this.workload) {
        this.populateWorkloadById(this.workloadId).catch(function (error) {
          console.log(error);
        });
      }
      if (
        this.workload &&
        this.workload.timePeriodId &&
        this.isWorkloadsManager
      ) {
        workloadsApi
          .getWorkloadHistory(
            this.workload.asuriteId,
            this.workload.timePeriodId
          )
          .then(function (response) {
            that.workloadHistory = response.data;
          });
      }
      if (this.workload && this.workload.asuriteId && this.isWorkloadsManager) {
        this.loadNotes(this.workload.asuriteId);
        this.loadClassHistory(this.workload.asuriteId);
      }
      if (this.workload && this.workload.timePeriod) {
        this.loadCourseEquivalents(
          this.workload.timePeriod,
          this.workload.asuriteId
        );
      }
    }
  },

  created() {
    // ensure that the Vuex copy of the workload is current
    var that = this;
    this.populateWorkloadById(this.workloadId).catch(function (error) {
      console.log(error);
    });
    workloadsApi.getWorkloadTypeDefinitions().then(response => {
      that.workloadTypeDefinitions = response.data;
    });

    // if (this.workload.administrativeRequests.length) {
    //   this.selectedRequests = this.workload.administrativeRequests;
    //   this.totalRows = this.workload.administrativeRequests.length;
    // }
  }
};
</script>

<style scoped>
.btn-primary {
  background-color: #8c1d40;
  border-color: #70112f;
}

.btn-primary:hover {
  background-color: #a02048;
}

.non-contributing {
  color: red;
  border-color: red;
}
</style>
